import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Clock, Check, HandPlatter, Target, BellRing, KeyRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import NotificationBell from '../components/NotificationBell';
import QuickLinksModal from '../components/QuickLinksModal';
import ServiceRequestListener from '../components/ServiceRequestListener';
import AlertPrefsButton from '../components/AlertPrefsButton';
import PanelViewToggle from '../components/PanelViewToggle';
import AllLiveOrdersView from '../components/AllLiveOrdersView';
import CoalRoundBanner from '../components/CoalRoundBanner';
import StaffHeaderMenu from '../components/StaffHeaderMenu';
import TableFloorView from '../components/TableFloorView';
import { useOrderAlert } from '../lib/orderAlert';
import { tableLabel } from '../lib/tableLabel';

function getElapsedMinutes(timestamp) {
  return Math.floor((Date.now() - new Date(timestamp).getTime()) / 60000);
}

export default function WaiterPanel() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [updating, setUpdating] = useState('');
  const [quickLinksOpen, setQuickLinksOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = localStorage.getItem('majnu:panelView:serving');
      return v === 'all' ? 'all' : 'mine';
    } catch { return 'mine'; }
  });
  const onChangeViewMode = (v) => {
    setViewMode(v);
    try { localStorage.setItem('majnu:panelView:serving', v); } catch { /* sandboxed */ }
  };

  const [allTables, setAllTables] = useState([]);

  const fetchTables = useCallback(async () => {
    try {
      // Use /tables/overview (same endpoint the POS till uses) so the
      // service panel renders the SAME state — active_tabs, occupancy,
      // settled_seated stamp, etc. The plain /tables endpoint returns
      // bare table docs without active_tabs, which caused the waiter
      // panel to show occupied tables as Available (Feb 2026 owner-
      // reported integration bug between till + service panel).
      const { data } = await api.get('/tables/overview');
      setAllTables((data || []).sort((a, b) => a.number - b.number));
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    // 5s cadence so the floor view picks up clearing → available flips
    // (driven by table.status from the backend) close to real-time.
    fetchTables();
    const id = setInterval(fetchTables, 5000);
    const onVis = () => { if (document.visibilityState === 'visible') fetchTables(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchTables]);

  // Owner ask Feb 2026: the serving panel doesn't take orders anymore.
  // The floor view is purely a tool for waiters to clear tables and
  // mark them available. Taps on tiles:
  //   - free      → toast (already free, nothing to do)
  //   - occupied  → confirm → POST /tables/{id}/clear (guard-railed by
  //                 the backend's bill-settled check; we surface the
  //                 friendly 400 verbatim)
  //   - clearing  → same as occupied (free up the table)
  const clearTable = async (table) => {
    if (!window.confirm(`Mark T${table.number} as cleared (available)?`)) return;
    try {
      await api.post(`/tables/${table.id}/clear`);
      toast.success(`T${table.number} → available`);
      fetchTables();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed', { duration: 5000 });
    }
  };
  const noopFreeTile = () => {
    toast.info('Table is already free', { duration: 1500 });
  };

  const fetchOrders = useCallback(async () => {
    try {
      const { data } = await api.get('/orders/panel/serving');
      setOrders(data);
    } catch { /* fetch failed */ }
  }, []);

  useEffect(() => {
    fetchOrders();
    const i = setInterval(fetchOrders, 5000);
    const onVisibility = () => { if (document.visibilityState === 'visible') fetchOrders(); };
    const onFocus = () => fetchOrders();
    const onOnline = () => fetchOrders();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    return () => {
      clearInterval(i);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, [fetchOrders]);

  // Waiter looping chime: rings while at least one item still needs attention
  // (activities in received OR food/drinks/shisha in ready). Stops the moment
  // the waiter advances anything.
  const { prefs, updatePrefs, playSample } = useOrderAlert({
    role: 'waiter',
    orders,
    getReceivedItems: (o) => {
      const items = o.items || [];
      const allUntouched = items.length > 0 && items.every(i => i.status === 'received' || i.status === 'ready');
      return allUntouched ? items : [];
    },
  });

  const advanceItem = async (orderId, itemId, nextStatus, label) => {
    setUpdating(`${orderId}-${itemId}`);
    try {
      await api.put(`/orders/${orderId}/items/${itemId}/status`, { status: nextStatus });
      toast.success(label);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setUpdating('');
    }
  };

  const totalItems = orders.reduce((sum, o) => sum + o.items.length, 0);
  const activitiesPending = orders.reduce((sum, o) => sum + o.items.filter(i => i.status === 'received').length, 0);

  // Public-kiosk mode — when the panel is hit without an auth session
  // (Feb 2026 owner ask: any staff member should be able to clock in /
  // out from the serving panel without needing the tablet to be signed
  // in as that exact person). The unauthenticated landing now points
  // staff at the dedicated `/kiosk` page rather than embedding the
  // widget inline, so the experience matches the in-panel "Kiosk"
  // button (one consistent flow). Orders + floor view are hidden
  // because they require a logged-in user.
  if (!user) {
    return (
      <div className="min-h-screen bg-bg-primary flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md space-y-5">
          <div className="text-center space-y-1">
            <div className="inline-flex items-center gap-2">
              <HandPlatter className="w-5 h-5 text-emerald-300" />
              <h1 className="font-serif text-2xl text-white">Serving Panel</h1>
            </div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans">
              Tablet is signed out
            </p>
          </div>
          <Link
            to="/kiosk"
            className="block w-full text-center bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-200 rounded-sm font-sans text-sm uppercase tracking-[0.16em] py-4"
            data-testid="kiosk-launch-link"
          >
            <KeyRound className="w-4 h-4 inline-block mr-2 -mt-0.5" />
            Open Kiosk · Clock In / Out
          </Link>
          <p className="text-center text-[11px] text-zinc-500 font-sans">
            Orders + floor view will appear once a waiter / bartender /
            manager signs in to this device.
          </p>
          <div className="text-center">
            <a
              href="/login"
              className="inline-flex items-center gap-1.5 text-xs font-sans uppercase tracking-[0.14em] text-gold/80 hover:text-gold"
              data-testid="kiosk-sign-in-link"
            >
              Sign in →
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      <ServiceRequestListener />
      {/* Focused header — owner ask Feb 2026: serving panel should look
          like the bar / kitchen / shisha "focused" layout (icon + title
          breadcrumb + single SERVE QUEUE pill below, no Take Order or
          cross-panel jump buttons). The serving panel no longer takes
          orders; its only floor responsibility is clearing tables and
          marking them available. */}
      <header className="sticky top-0 z-30 glass-header px-3 py-2.5 sm:px-4 sm:py-3 flex items-center justify-between gap-3 flex-wrap border-b border-white/10">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-sm bg-emerald-500/10 flex items-center justify-center shrink-0">
            <HandPlatter className="w-5 h-5 text-emerald-300" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base sm:text-lg font-serif text-white truncate leading-none" data-testid="panel-title">Serving Panel</h1>
              <span className="text-zinc-600">›</span>
              <span className="text-base sm:text-lg font-serif leading-none text-emerald-300" data-testid="panel-current-section">
                Serve Queue
              </span>
            </div>
            <p className="text-[10px] sm:text-xs text-zinc-500 font-sans mt-1 truncate" data-testid="panel-subtitle">
              Items waiting to be served · {user?.name}
            </p>
          </div>
          {activitiesPending > 0 && (
            <Badge className="bg-blue-accent/15 text-blue-accent border border-blue-accent/30 font-sans text-[10px] uppercase tracking-[0.15em]" data-testid="tables-to-setup-count">
              {activitiesPending} table{activitiesPending === 1 ? '' : 's'} to set up
            </Badge>
          )}
        </div>

        {/* Controls — trimmed to match the focused look. Take-Order is
            gone (serving panel no longer takes orders). Manager/director
            can still toggle the cross-panel "all live orders" view.
            Feb 2026 owner ask: replaced the inline kiosk widget with a
            dedicated "Kiosk" button that opens /kiosk in a new tab so
            any staff member can clock in/out without disturbing whoever
            is currently signed in on this tablet. */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end shrink-0">
          <a
            href="/kiosk"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-200 rounded-sm font-sans text-xs uppercase tracking-[0.14em]"
            data-testid="kiosk-open-btn"
            title="Open the staff clock-in / clock-out kiosk in a new tab"
          >
            <KeyRound className="w-4 h-4" />
            Kiosk
          </a>
          {['manager', 'director'].includes(user?.role) && (
            <PanelViewToggle value={viewMode} onChange={onChangeViewMode} />
          )}
          <AlertPrefsButton prefs={prefs} onChange={updatePrefs} onTest={playSample} />
          <NotificationBell />
          <StaffHeaderMenu
            onOpenQuickLinks={() => setQuickLinksOpen(true)}
            onLogout={logout}
          />
        </div>
      </header>

      {/* Big SERVE QUEUE pill — mirrors the PrepPanel's PREP QUEUE strip
          so all four staff prep surfaces (bar · kitchen · shisha ·
          serving) share one visual language. */}
      <div
        className="sticky top-[68px] z-20 w-full border-b border-white/10 bg-bg-primary/95 backdrop-blur-md px-2 py-2"
        data-testid="serve-queue-tabstrip"
      >
        <div
          className="inline-flex items-center gap-2 text-[12px] sm:text-sm uppercase tracking-[0.12em] h-11 px-3.5 sm:px-4 rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/50"
          data-testid="serve-queue-tab"
        >
          <HandPlatter className="w-4 h-4" />
          <span className="font-semibold">Serve Queue</span>
          <Badge className="ml-0.5 font-mono text-[10px] px-1.5 bg-white/5 text-zinc-300 border border-white/10" data-testid="serve-queue-count">
            {totalItems} active
          </Badge>
        </div>
      </div>

      <QuickLinksModal open={quickLinksOpen} onClose={() => setQuickLinksOpen(false)} />

      {/* Content */}
      {viewMode === 'all' ? (
        <AllLiveOrdersView myPanelKey="serving" />
      ) : (
      <div className="p-4 pb-32">
        {orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <HandPlatter className="w-12 h-12 text-zinc-700 mb-4" />
            <p className="text-zinc-500 font-sans" data-testid="no-orders-message">No items to serve</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
            {orders.map(order => (
              <div key={order.id} className="bg-white/[0.03] border border-emerald-500/10 rounded-sm p-4" data-testid={`serving-order-${order.id}`}>
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-gold font-serif text-2xl font-semibold">{tableLabel(order.table_number)}</span>
                    <span className="text-sm text-zinc-500 font-sans">#{order.id.slice(0, 6)}</span>
                    {order.customer_name && <span className="text-sm text-zinc-300 font-sans truncate">· {order.customer_name}</span>}
                  </div>
                  <span className="text-sm text-zinc-400 font-mono flex items-center gap-1">
                    <Clock className="w-4 h-4" /> {getElapsedMinutes(order.created_at)}m
                  </span>
                </div>
                <div className="space-y-2">
                  {order.items.map(item => {
                    const isActivity = (item.prep_panel || item.category) === 'activities';
                    const busy = updating === `${order.id}-${item.id}`;
                    // Activity in 'received' → waiter taps "Table Ready" (→ ready)
                    // Activity in 'ready'    → waiter taps "Customer Arrived" (→ served)
                    // Food/drink/shisha in 'ready' → waiter taps "Served" (→ served)
                    if (isActivity && item.status === 'received') {
                      return (
                        <div key={item.id} className="flex items-center justify-between py-2 border-t border-white/5" data-testid={`activity-pending-${item.id}`}>
                          <div className="min-w-0">
                            <span className="text-white font-sans text-base flex items-center gap-1.5">
                              <Target className="w-4 h-4 text-blue-accent shrink-0" />
                              {item.quantity}x {item.name}
                            </span>
                            <Badge className="mt-1 bg-blue-accent/15 text-blue-accent border-0 font-sans text-[10px] uppercase tracking-wider">Set up table</Badge>
                          </div>
                          <Button
                            size="sm"
                            disabled={busy}
                            onClick={() => advanceItem(order.id, item.id, 'ready', 'Customer notified — table is ready')}
                            className="h-9 bg-blue-accent/15 text-blue-accent border border-blue-accent/40 hover:bg-blue-accent/25 rounded-sm font-sans text-sm px-3"
                            data-testid={`mark-table-ready-${item.id}`}
                          >
                            <BellRing className="w-4 h-4 mr-1" /> Table Ready
                          </Button>
                        </div>
                      );
                    }
                    if (isActivity && item.status === 'ready') {
                      return (
                        <div key={item.id} className="flex items-center justify-between py-2 border-t border-white/5" data-testid={`activity-ready-${item.id}`}>
                          <div className="min-w-0">
                            <span className="text-white font-sans text-base flex items-center gap-1.5">
                              <Target className="w-4 h-4 text-blue-accent shrink-0" />
                              {item.quantity}x {item.name}
                            </span>
                            <Badge className="mt-1 bg-emerald-500/15 text-emerald-400 border-0 font-sans text-[10px] uppercase tracking-wider">Customer notified</Badge>
                          </div>
                          <Button
                            size="sm"
                            disabled={busy}
                            onClick={() => advanceItem(order.id, item.id, 'served', 'Marked as served')}
                            className="h-9 bg-gold text-black font-semibold hover:bg-gold-light rounded-sm font-sans text-sm px-3"
                            data-testid={`mark-arrived-${item.id}`}
                          >
                            <Check className="w-4 h-4 mr-1" /> Customer Arrived
                          </Button>
                        </div>
                      );
                    }
                    // Default: food/drinks/shisha in 'ready' — deliver and mark served
                    return (
                      <div key={item.id} className="flex items-center justify-between py-2 border-t border-white/5">
                        <div>
                          <span className="text-white font-sans text-base">{item.quantity}x {item.name}</span>
                          <Badge className="ml-2 bg-emerald-500/20 text-emerald-400 border-0 font-sans text-xs">{item.category}</Badge>
                        </div>
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => advanceItem(order.id, item.id, 'served', 'Marked as served')}
                          className="h-9 bg-gold text-black font-semibold hover:bg-gold-light rounded-sm font-sans text-sm px-3"
                          data-testid={`mark-served-${item.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Served
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tables — full floor view BELOW the serve queue. Owner ask
            Feb 2026: this floor is **only** for clearing tables and
            marking them available. Tap occupied / clearing → confirm →
            `/tables/{id}/clear` (still guarded by the backend's
            bill-settled check); tap free → friendly no-op toast. */}
        <section className="mt-8" data-testid="serving-tables-section">
          <div className="flex items-center justify-between mb-3 px-1">
            <h2 className="text-sm font-serif text-white uppercase tracking-[0.18em]">Floor</h2>
            <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-600 font-sans hidden sm:inline">
              Tap an occupied / clearing tile to free it · live every 5s
            </span>
          </div>
          <TableFloorView
            tables={allTables}
            loading={false}
            onAvailable={noopFreeTile}
            onOccupied={clearTable}
            onCleaning={clearTable}
            testId="serving-floor-view"
          />
        </section>
      </div>
      )}
      <CoalRoundBanner />
    </div>
  );
}
